import { Box, Button, styled, Theme, Typography } from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { useToggle } from "react-use";
import ChevronDownIcon from "../../../components/icons/chevron-down";
import LocalizedLink from "../../../components/localized-link";
import { formatPrice } from "../../../helpers/price";
import { getDefaultProductVariant } from "../../../helpers/product";
import { useExpressDeliveryAvailabilitySelector } from "../../../hooks/use-express-delivery-availability";
import useFeatureFlag from "../../../hooks/use-feature-flag";
import useSubcountry from "../../../hooks/use-subcountry";
import useTranslate from "../../../hooks/use-translate";
import { ProductType } from "../../../types/airgraft";
import { RetailProviderItem } from "./components/RetailProviderItem";
import { XpressProviderItem } from "./components/XpressProviderItem";
import useRetailProvidersProductAvailability from "./hooks/use-retail-providers-product-availability";
import StoreLocatorIconSvg from "./store-locator-icon.svg";
import {
  filterRetailProvidersForProduct,
  sortRetailProvidersByClosestToAddress
} from "./utils";

const RetailBuyingOptionsList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  "& > div:first-child": {
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px"
  },
  "& > div:last-child": {
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px"
  }
}));

const ShowMoreLessRetailersButton = styled(Button)(
  ({ theme, withRoundBorder }: { theme: Theme; withRoundBorder: boolean }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.grey[100],
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    maxHeight: "40px",
    height: "40px",
    width: "100%",
    border: "0px",
    color: theme.palette.text.secondary,
    borderRadius: withRoundBorder ? "0 0 8px 8px" : "0px",
    "&:hover": {
      backgroundColor: theme.palette.grey[100]
    }
  })
);

type Props = {
  product: ProductType;
};

export default function ProductBuyingOptions({ product }: Props) {
  const t = useTranslate();
  const { subcountry } = useSubcountry();
  const isStoreLocatorEnabled = useFeatureFlag("STORE_LOCATOR");
  const isXpressEnabled = useFeatureFlag("SHOP_XPRESS");
  const productVariant = getDefaultProductVariant(product);
  const availableProvider = useExpressDeliveryAvailabilitySelector(
    c => c.availableProvider
  );

  // Query all retail providers
  const {
    allDatoCmsRetailProvider: { nodes: retailProviders }
  } = useStaticQuery<Queries.ProductRetailProvidersFragmentQuery>(query);

  // Filter retail providers enabled for product brand and current subcountry
  let filteredAndSortedRetailProviders = filterRetailProvidersForProduct(
    retailProviders,
    productVariant,
    subcountry
  );

  // Sort closest retail providers to user delivery address
  const { address: userDeliveryAddress } =
    useExpressDeliveryAvailabilitySelector(c => ({
      address: c.address
    }));
  if (userDeliveryAddress) {
    filteredAndSortedRetailProviders = sortRetailProvidersByClosestToAddress(
      filteredAndSortedRetailProviders,
      userDeliveryAddress
    );
  }

  // Fetch product availability from listed retail providers
  const productAvailabilityQueryResults = useRetailProvidersProductAvailability(
    product.originalId,
    filteredAndSortedRetailProviders
      .filter(p => !!p.apiType)
      .map(p => p.originalId)
  );

  // Expand/Minimize retail providers list
  const listSizeLimit = isXpressEnabled ? 0 : 3;
  const isListTooLong = filteredAndSortedRetailProviders.length > listSizeLimit;
  const [isListExpanded, toggleListExpanded] = useToggle(false);

  // Hide list if empty
  if (filteredAndSortedRetailProviders.length === 0) {
    return null;
  }

  return (
    <Box marginTop={{ xs: 2, md: 4 }}>
      {/* "Buy At" label */}
      <Box marginLeft={2} marginBottom={0.5}>
        <Typography className="compact" color="textSecondary">
          {t("pdp.buyAt")}
        </Typography>
      </Box>

      {/* Buying options list */}
      <RetailBuyingOptionsList>
        {/* XPRESS delivery option inside list */}
        {isXpressEnabled && <XpressProviderItem product={product} />}

        {/* Buying options list */}
        {filteredAndSortedRetailProviders
          .slice(0, isListExpanded ? 100 : listSizeLimit)
          .map(retailProvider => {
            const productAvailability =
              productAvailabilityQueryResults[retailProvider.originalId];
            return (
              <RetailProviderItem
                key={`${retailProvider.originalId}-${
                  productAvailability ? "available" : "unavailable"
                }`}
                retailProvider={retailProvider}
                availability={productAvailability?.data}
                isLoadingAvailability={productAvailability?.isFetching}
              />
            );
          })}
      </RetailBuyingOptionsList>

      {/* Expand/Minimize list button */}
      {isListTooLong && (
        <ShowMoreLessRetailersButton
          onClick={toggleListExpanded}
          withRoundBorder={!isStoreLocatorEnabled}
        >
          <Typography variant="body2" className="compact" color="textSecondary">
            {isListExpanded
              ? t("shop.showLess")
              : t("shop.showMoreRetailers", {
                  count: filteredAndSortedRetailProviders.length - listSizeLimit
                })}
          </Typography>

          <ChevronDownIcon
            color="disabled"
            fontSize="small"
            style={{
              transform: isListExpanded ? "rotate(180deg)" : "rotate(0deg)"
            }}
          />
        </ShowMoreLessRetailersButton>
      )}

      {/* Store locator button */}
      {isStoreLocatorEnabled && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="common.white"
          paddingX={{ xs: 2 }}
          paddingY={{ xs: 1 }}
          mb="1px"
          maxHeight="46px"
          height="46px"
          borderRadius="0 0 8px 8px"
          style={{ filter: "drop-shadow(2px 4px 4px rgba(0, 0, 0, 0.04))" }}
          component={LocalizedLink}
          width="100%"
          border={0}
          // @ts-ignore
          to={"/store-locator"}
        >
          <Box display="flex" alignItems="center">
            <Box mr={1} display="flex" alignContent="center">
              <img src={StoreLocatorIconSvg} width={24} height={24} />
            </Box>
            <Typography variant="body1" className="compact">
              {t("pdp.storelocator")}
            </Typography>
          </Box>
          <ArrowForward />
        </Box>
      )}

      {/* XPRESS "Free delivery over 50$" label */}
      {isXpressEnabled && (
        <Box mt={1}>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ lineHeight: 1 }}
          >
            <strong className="express-delivery">
              {t("pdp.xpressFreeDeliveryOver", {
                minimumTotal: formatPrice(
                  {
                    amount:
                      availableProvider?.deliveryFreeDeliveryMinimum ||
                      availableProvider?.deliveryTotalMinimum ||
                      50
                  },
                  {
                    hideZeros: true
                  }
                )
              })}
            </strong>
            &nbsp;{t("pdp.xpressFullfiled")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const query = graphql`
  query ProductRetailProvidersFragment {
    allDatoCmsRetailProvider(
      filter: { locale: { eq: "en-US" }, enabled: { eq: true } }
      sort: { fields: position }
    ) {
      nodes {
        originalId
        position
        apiType
        title
        url
        logo {
          url
        }
        enabledBrands {
          slug
        }
        enabledSubcountries {
          slug
        }
        geolocation {
          latitude
          longitude
        }
      }
    }
  }
`;
